import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { normalizeBaseUrl } from '../utils/url.utils';

export type ApiRequestOptions = {
  headers?: HttpHeaders | Record<string, string | string[]>;
  params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;
  context?: HttpContext;
};

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = normalizeBaseUrl(environment.apiBaseUrl);

  get<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(this.buildUrl(path), this.normalizeOptions(options));
  }

  post<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.post<T>(this.buildUrl(path), body, this.normalizeOptions(options));
  }

  put<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(this.buildUrl(path), body, this.normalizeOptions(options));
  }

  patch<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.patch<T>(this.buildUrl(path), body, this.normalizeOptions(options));
  }

  delete<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(this.buildUrl(path), this.normalizeOptions(options));
  }

  private buildUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}`;
  }

  private normalizeOptions(options?: ApiRequestOptions): ApiRequestOptions | undefined {
    if (!options?.params || options.params instanceof HttpParams) {
      return options;
    }

    const cleanedParams = Object.entries(options.params).reduce<Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>>(
      (acc, [key, value]) => {
        if (value === undefined || value === null || value === '' || value === 'undefined') {
          return acc;
        }
        acc[key] = value;
        return acc;
      },
      {},
    );

    return {
      ...options,
      params: cleanedParams,
    };
  }
}
